import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useNamespaces } from '../lib/hooks'
import { api } from '../lib/api'

export function Namespaces() {
  const { data, loading, error, refetch } = useNamespaces()
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm]             = useState({ slug: '', name: '', is_private: true })
  const [busy, setBusy]             = useState(false)
  const [feedback, setFeedback]     = useState<{ ok: boolean; msg: string } | null>(null)

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFeedback(null)
    try {
      await api.post('/api/namespaces', form)
      setFeedback({ ok: true, msg: `Namespace "${form.slug}" created.` })
      setForm({ slug: '', name: '', is_private: true })
      setShowCreate(false)
      refetch()
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Layout
      title="Namespaces"
      actions={
        <button className="btn btn-primary" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? 'Cancel' : '+ New namespace'}
        </button>
      }
    >
      {showCreate && (
        <div className="card fu" style={{ marginBottom: 24 }}>
          <div className="card-header"><div className="kicker">New namespace</div></div>
          <div className="card-body">
            <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label className="field-label">slug</label>
                <input
                  className="input" placeholder="my-namespace" value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                  required pattern="[a-z][a-z0-9\-]{2,62}"
                />
              </div>
              <div>
                <label className="field-label">name</label>
                <input
                  className="input" placeholder="My Namespace" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} required
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="priv" checked={form.is_private}
                  onChange={(e) => setForm({ ...form, is_private: e.target.checked })}
                  style={{ accentColor: 'var(--accent)' }} />
                <label htmlFor="priv" style={{ fontSize: '0.825rem', color: 'var(--text2)' }}>
                  Private namespace
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? <span className="spinner" /> : 'Create'}
                </button>
              </div>
            </form>
            {feedback && (
              <div className={feedback.ok ? 'feedback-success' : 'feedback-error'} style={{ marginTop: 12 }}>
                {feedback.msg}
              </div>
            )}
          </div>
        </div>
      )}

      {loading && <div className="empty-state">Loading…</div>}
      {error   && <div className="empty-state" style={{ color: 'var(--red)' }}>{error}</div>}

      {(data?.namespaces.length ?? 0) > 0 && (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr><th>Namespace</th><th>Visibility</th><th>Modules</th><th>Members</th><th>Role</th><th></th></tr>
            </thead>
            <tbody>
              {data!.namespaces.map((n) => (
                <tr key={n.id}>
                  <td>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>{n.slug}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text2)' }}>{n.name}</div>
                  </td>
                  <td><span className={`badge ${n.is_private ? 'badge-neutral' : 'badge-green'}`}>{n.is_private ? 'private' : 'public'}</span></td>
                  <td style={{ color: 'var(--text2)' }}>{n.module_count}</td>
                  <td style={{ color: 'var(--text2)' }}>{n.member_count}</td>
                  <td><span className="badge badge-accent">{n.role}</span></td>
                  <td><Link to={`/namespaces/${n.slug}`} className="btn btn-ghost btn-sm">Open →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data?.namespaces.length === 0 && (
        <div className="empty-state fu">
          <div className="empty-icon">⬡</div>
          <div>No namespaces yet. Create one to get started.</div>
        </div>
      )}
    </Layout>
  )
}
